import { useEffect, useState, type ReactNode } from 'react';
import { Button, HStack, IconButton, Input, SimpleGrid, Stack, Text } from '@chakra-ui/react';
import type { Grid, Screen, TileStyle } from '@casa/model';
import { DEFAULT_TILE_STYLE, searchIcons } from '@casa/tiles';
import { ColorField } from '../forms/color_fields.tsx';
import { TextFieldRow } from '../forms/form_rows.tsx';
import { GRID_SPACING } from '../screens/grid.ts';
import { SettingsGroup, SettingsRow } from '../settings/settings_ui.tsx';
import { ChromeDialog } from '../ui/chrome_dialog.tsx';
import { ChromeNote } from '../ui/chrome_page.tsx';
import { IconGlyph } from '../ui/icon_glyph.tsx';
import { PAGE_BACKGROUND } from '../ui/theme.ts';
import { TILE_CATALOG, type CatalogType } from './tile_catalog.ts';
import { TileStyleFields } from './tile_style_fields.tsx';

// The dialogs of edit mode, in the settings' rows (look LK-7): the list of tile types to add a tile
// from (L-13), the screens (D-4) with the icon picker and "Save as" (S-2), and the screen to move a
// tile to (L-12), and the dashboard's style (§5.11).

/** The most columns or rows a grid takes, as a new dashboard's (new_dashboard_form.tsx). */
const MAX_CELLS = 200;

/** The tile types, each with what it is for; a choice adds a tile of that type (L-13). */
export function TilePicker({ onPick, onClose }: { onPick: (type: CatalogType) => void; onClose: () => void }) : ReactNode {
    return (
        <ChromeDialog title="Add a tile" onClose={onClose} focusesField={false}>
            <SettingsGroup>
                {TILE_CATALOG.map((entry) => (
                    <SettingsRow key={entry.type} icon={entry.icon} title={entry.name} detail={entry.description} opens={false} onClick={() => onPick(entry.type)} />
                ))}
            </SettingsGroup>
            <Text color="fg.muted" fontSize="sm" mt="3">
                Its style, title and data are chosen next.
            </Text>
        </ChromeDialog>
    );
}

/**
 * A search of the icon set by name (D-4); a choice picks the icon. `none` names the choice of no
 * icon of its own ("No icon", or the default's name).
 */
export function IconSearch({ onPick, none = 'No icon', autoFocus = false }: { onPick: (name: string | undefined) => void; none?: string; autoFocus?: boolean }) : ReactNode {
    const [query, setQuery] = useState('');
    const [found, setFound] = useState<string[]>([]);

    useEffect(() => {
        let isCurrent = true;
        const search = async () : Promise<void> => {
            const names = await searchIcons(query, 60);
            if (isCurrent) {
                setFound(names);
            }
        };
        void search();

        return () => {
            isCurrent = false;
        };
    }, [query]);

    return (
        <Stack gap="3">
            <HStack>
                <Input autoFocus={autoFocus} aria-label="Search icons" placeholder="Search by name: house, sun, garage…" value={query} onChange={(event) => setQuery(event.target.value)} />
                <Button variant="outline" onClick={() => onPick(undefined)}>
                    {none}
                </Button>
            </HStack>
            <SimpleGrid minChildWidth="80px" gap="2">
                {found.map((name) => (
                    <Button key={name} title={name} variant="subtle" height="auto" py="2" flexDirection="column" gap="1" onClick={() => onPick(name)}>
                        <IconGlyph name={name} size={32} />
                        <Text fontSize="2xs" color="fg.muted" whiteSpace="normal" overflowWrap="anywhere">
                            {name}
                        </Text>
                    </Button>
                ))}
            </SimpleGrid>
            {query.trim() !== '' && found.length === 0 ? <Text color="fg.muted">No icon has that in its name.</Text> : null}
        </Stack>
    );
}

/** The icon search in a dialog of its own. */
export function IconPicker({ onPick, onClose }: { onPick: (name: string | undefined) => void; onClose: () => void }) : ReactNode {
    return (
        <ChromeDialog title="Choose an icon" onClose={onClose} size="lg">
            <IconSearch onPick={onPick} autoFocus />
        </ChromeDialog>
    );
}

export interface DashboardStyleDialogProps {
    background: string | undefined;
    grid: Grid;
    /** The grid as saved: columns and rows are never fewer (DS-2). */
    least: Grid;
    style: TileStyle;
    known: readonly string[];
    onBackground: (background: string | undefined) => void;
    onGrid: (grid: Grid) => void;
    onStyle: (style: TileStyle) => void;
    onClose: () => void;
}

/**
 * The dashboard's look (dashboard-editor spec §5.11, DS-7): its background, its grid (made larger,
 * the margin and spacing), and the tile style every tile takes unless it sets its own (§5.9, TS-2).
 */
export function DashboardStyleDialog({ background, grid, least, style, known, onBackground, onGrid, onStyle, onClose }: DashboardStyleDialogProps) : ReactNode {
    const [refused, setRefused] = useState<string>();
    // Counted, so that a refused field is drawn anew with the grid's value, not left showing what was refused.
    const [refusals, setRefusals] = useState(0);
    // A whole number of cells, at least the saved grid's; what is refused stays as it was, and says why.
    const cells = (key: 'columns' | 'rows', typed: string | undefined) : void => {
        const minimum = least[key] ?? 1;
        const number = Number(typed);
        if (typed === undefined || !Number.isInteger(number) || number < minimum || number > MAX_CELLS) {
            setRefused(`${key === 'columns' ? 'Columns' : 'Rows'}: a whole number from ${minimum} to ${MAX_CELLS}. The grid can be made larger, not smaller.`);
            setRefusals((count) => count + 1);

            return;
        }
        setRefused(undefined);
        onGrid({ ...grid, [key]: number });
    };
    const pixels = (key: 'margin' | 'spacing', typed: string | undefined) : void => {
        const number = typed === undefined ? undefined : Number(typed);
        if (number !== undefined && !(Number.isFinite(number) && number >= 0)) {
            return;
        }
        const { [key]: _old, ...rest } = grid;
        onGrid(number === undefined ? rest : { ...rest, [key]: number });
    };

    return (
        <ChromeDialog title="Dashboard style" onClose={onClose} focusesField={false} footer={<Button colorPalette="blue" onClick={onClose}>Done</Button>}>
            <Stack gap="6">
                <Text fontSize="sm" color="fg.muted" px="1">
                    The dashboard&apos;s look, and the style every tile takes unless it sets its own (Edit… on a tile, Tile style). Kept with Save, like every other edit.
                </Text>
                <SettingsGroup title="Screens">
                    <ColorField label="Background" description="Behind the tiles of every screen." value={background} fallback={PAGE_BACKGROUND} known={known} onChange={onBackground} />
                </SettingsGroup>
                <SettingsGroup title="Grid" note="More columns or rows make every tile smaller: tiles keep their cells. Margin and spacing in pixels; empty: the default.">
                    <TextFieldRow key={`columns-${grid.columns}-${refusals}`} label="Columns" type="number" value={String(grid.columns)} onChange={(typed) => cells('columns', typed)} />
                    {grid.rows === undefined ? null : <TextFieldRow key={`rows-${grid.rows}-${refusals}`} label="Rows" type="number" value={String(grid.rows)} onChange={(typed) => cells('rows', typed)} />}
                    <TextFieldRow label="Margin" detail="From the screen's edges" type="number" value={grid.margin === undefined ? undefined : String(grid.margin)} placeholder={`Default (${GRID_SPACING.margin})`} onChange={(typed) => pixels('margin', typed)} />
                    <TextFieldRow label="Spacing" detail="Between tiles" type="number" value={grid.spacing === undefined ? undefined : String(grid.spacing)} placeholder={`Default (${GRID_SPACING.spacing})`} onChange={(typed) => pixels('spacing', typed)} />
                </SettingsGroup>
                {refused ? <ChromeNote status="warning" text={refused} /> : null}
                <TileStyleFields style={style} fallback={DEFAULT_TILE_STYLE} known={known} hasText hasGauge hasValue onChange={onStyle} />
            </Stack>
        </ChromeDialog>
    );
}

export interface ScreensDialogProps {
    screens: readonly Screen[];
    onAdd: () => void;
    onRename: (index: number, name: string) => void;
    onIcon: (index: number, icon: string | undefined) => void;
    onMove: (index: number, by: -1 | 1) => void;
    onDelete: (index: number) => void;
    /** The working copy saved under a new name on the Tilez Hub (S-2). */
    onSaveAs: () => void;
    onClose: () => void;
}

/**
 * The screens of the dashboard as rows (D-4, look LK-7): a tap on one shows its actions under it
 * (name, icon, order, delete; never the last). A name that another screen has, or none, is not
 * taken (tiles spec TA-5): the field says so, and the screen keeps its name.
 */
export function ScreensDialog({ screens, onAdd, onRename, onIcon, onMove, onDelete, onSaveAs, onClose }: ScreensDialogProps) : ReactNode {
    const [iconFor, setIconFor] = useState<number>();
    const [chosen, setChosenIndex] = useState<number>();
    // The chosen screen's name as typed, which differs from its name while it is not taken.
    const [typed, setTyped] = useState<string>();
    const setChosen = (index: number | undefined) : void => {
        setChosenIndex(index);
        setTyped(undefined);
    };
    const refusal = (index: number, name: string) : string | undefined => {
        if (name === '') {
            return `A screen needs a name; it stays "${screens[index]?.name ?? ''}".`;
        }

        return screens.some((screen, other) => other !== index && screen.name === name) ? `Another screen is called "${name}"; this one stays "${screens[index]?.name ?? ''}".` : undefined;
    };

    return (
        <>
            <ChromeDialog
                title="Screens"
                onClose={onClose}
                focusesField={false}
                footer={
                    <>
                        <Button variant="outline" onClick={onSaveAs}>
                            Save as a new dashboard…
                        </Button>
                        <Button colorPalette="blue" onClick={() => {
                            onAdd();
                            setChosen(screens.length);
                        }}>
                            Add a screen
                        </Button>
                    </>
                }
            >
                <SettingsGroup note="Each screen has a name of its own: a tile's tap goes to a screen by its name. The last screen cannot be deleted.">
                    {screens.map((screen, index) => (
                        <Stack key={index} gap="0">
                            <SettingsRow
                                icon={screen.icon ?? 'square'}
                                title={screen.name || 'No name'}
                                detail={index === chosen ? undefined : `Screen ${index + 1}`}
                                isSelected={index === chosen}
                                opens={false}
                                onClick={() => setChosen(index === chosen ? undefined : index)}
                            />
                            {index === chosen && (
                                <Stack gap="2" px="4" pb="3" bg="bg.emphasized">
                                    <HStack gap="2">
                                        <IconButton aria-label="Choose an icon" title="Choose an icon" variant="outline" bg="bg.panel" onClick={() => setIconFor(index)}>
                                            <IconGlyph name={screen.icon ?? 'square'} size={22} color={screen.icon ? '#ffffff' : '#606060'} />
                                        </IconButton>
                                        <Input
                                            aria-label="Screen name"
                                            bg="bg.panel"
                                            value={typed ?? screen.name}
                                            onChange={(event) => {
                                                setTyped(event.target.value);
                                                onRename(index, event.target.value);
                                            }}
                                        />
                                    </HStack>
                                    {typed !== undefined && refusal(index, typed) ? (
                                        <Text fontSize="sm" color="orange.300" role="alert">
                                            {refusal(index, typed)}
                                        </Text>
                                    ) : null}
                                    <HStack gap="2" flexWrap="wrap">
                                        <Button size="sm" variant="subtle" disabled={index === 0} onClick={() => {
                                            onMove(index, -1);
                                            setChosen(index - 1);
                                        }}>
                                            ↑ Up
                                        </Button>
                                        <Button size="sm" variant="subtle" disabled={index === screens.length - 1} onClick={() => {
                                            onMove(index, 1);
                                            setChosen(index + 1);
                                        }}>
                                            ↓ Down
                                        </Button>
                                        <Button size="sm" colorPalette="red" variant="subtle" ms="auto" disabled={screens.length <= 1} onClick={() => {
                                            onDelete(index);
                                            setChosen(undefined);
                                        }}>
                                            Delete
                                        </Button>
                                    </HStack>
                                </Stack>
                            )}
                        </Stack>
                    ))}
                </SettingsGroup>
            </ChromeDialog>
            {iconFor !== undefined && (
                <IconPicker
                    onClose={() => setIconFor(undefined)}
                    onPick={(name) => {
                        onIcon(iconFor, name);
                        setIconFor(undefined);
                    }}
                />
            )}
        </>
    );
}

/** The other screens, to move a tile to (L-12). */
export function ScreenPicker({ screens, current, onPick, onClose }: { screens: readonly Screen[]; current: number; onPick: (index: number) => void; onClose: () => void }) : ReactNode {
    return (
        <ChromeDialog title="Move to screen" onClose={onClose} size="sm" focusesField={false}>
            {screens.length < 2 ? null : (
                <SettingsGroup>
                    {screens.map((screen, index) =>
                        index === current ? null : (
                            <SettingsRow key={index} icon={screen.icon ?? 'square'} title={screen.name} opens={false} onClick={() => onPick(index)} />
                        ),
                    )}
                </SettingsGroup>
            )}
            {screens.length < 2 ? <Text color="fg.muted">The dashboard has one screen; add another under Screens.</Text> : null}
        </ChromeDialog>
    );
}
